// @intent: Shared logical-playable-viewport to rendered-device-viewport coordinate mapping
// @flow: parent editor maps slot overlays; playable runtime maps Pixi scene root with the same transform
// @stale: when R6 introduces aspect-fit/letterbox or per-scene camera semantics

// @intent: Width/height pair for logical project or concrete rendered viewport sizes
// @flow: createViewportTransform consumes logical + rendered ViewportSize values
// @stale: when viewports need orientation metadata beyond dimensions
export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

// @intent: Rect in project logical coordinates or rendered device coordinates
// @flow: mapRectToViewport reads logical rects and returns rendered rects
// @stale: when coordinate spaces gain rotation or camera projection
export interface ViewportRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

// @intent: Deterministic transform shared by parent overlays and runtime scene root
// @flow: created from project manifest viewport + iframe viewport; consumed by rect mapping and Pixi root scale
// @stale: when runtime switches from stretch scaling to aspect-fit letterboxing
export interface ViewportTransform {
  readonly scaleX: number;
  readonly scaleY: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

function roundCoordinate(value: number): number {
  return Math.round(value * 10) / 10;
}

// @intent: Create stretch transform from logical project viewport to concrete iframe viewport
// @flow: CanvasV2 and RuntimeCanvas call this with the same inputs to stay visually aligned
// @stale: when the editor supports aspect-fit, crop, or camera modes
export function createViewportTransform(
  logicalViewport: ViewportSize,
  renderedViewport: ViewportSize,
): ViewportTransform {
  return {
    scaleX: renderedViewport.width / logicalViewport.width,
    scaleY: renderedViewport.height / logicalViewport.height,
    offsetX: 0,
    offsetY: 0,
  };
}

// @intent: Map a logical rectangle through the shared viewport transform
// @flow: CanvasV2 slot overlays use this; tests compare it with runtime layer transforms
// @stale: when subpixel precision becomes required for overlay rendering
export function mapRectToViewport(
  rect: ViewportRect,
  transform: ViewportTransform,
): ViewportRect {
  return {
    x: roundCoordinate(transform.offsetX + rect.x * transform.scaleX),
    y: roundCoordinate(transform.offsetY + rect.y * transform.scaleY),
    width: roundCoordinate(rect.width * transform.scaleX),
    height: roundCoordinate(rect.height * transform.scaleY),
  };
}
